import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  FiUser,
  FiMail,
  FiPhone,
  FiMapPin,
  FiShield,
  FiLock,
  FiEdit3,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiKey,
  FiActivity
} from "react-icons/fi";
import { HiOutlineBuildingOffice2 } from "react-icons/hi2";
import { ToastContainer, toast } from "react-toastify";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import methodModel from "../../methods/methods";
import Layout from "../../components/global/layout";

export default function ViewProfile() {
  const history = useNavigate();
  const reduxUser = useSelector((state) => state.user);

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch Super Admin Profile from user/admin-profile
  const fetchProfile = useCallback(async () => {
    setLoading(true);

    try {
      const res = await ApiClient.get("user/admin-profile");
      if (res?.success && (res?.data || res?.user_data)) {
        const data = res.data || res.user_data;
        setProfile(data);
      } else {
        // Fallback to redux user state if API fails
        setProfile(reduxUser);
      }
    } catch (err) {
      console.error("Failed to fetch admin profile:", err);
      setProfile(reduxUser);
    } finally {
      setLoading(false);
    }
  }, [reduxUser]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const fullName =
    profile?.fullName ||
    (profile?.firstName ? `${profile.firstName} ${profile.lastName || ""}` : "") ||
    reduxUser?.fullName ||
    "Super Admin";

  const email = profile?.email || reduxUser?.email || "superadmin@yopmail.com";
  const mobileNo = profile?.mobileNo || reduxUser?.mobileNo || "";
  const address = profile?.address || reduxUser?.address || "";
  const roleName = profile?.role?.name || reduxUser?.role?.name || "Super Admin";
  const status = profile?.status || "Active";
  const avatarUrl = profile?.image ? methodModel.userImg(profile.image) : "";

  // Date formatting helper
  const formatDate = (isoString) => {
    if (!isoString) return "N/A";
    try {
      const d = new Date(isoString);
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return isoString;
    }
  };

  return (
    <Layout>
      <div className="w-full max-w-full overflow-x-hidden space-y-6 pb-12 antialiased">

        {/* ======================================================== */}
        {/* 1. TOP BREADCRUMB & PAGE HEADER */}
        {/* ======================================================== */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-2 border-b border-slate-200">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 mb-1">
              <Link to="/dashboard" className="hover:text-[#115B9D] transition-colors">
                Dashboard
              </Link>
              <span>/</span>
              <span className="text-slate-800 font-bold">Profile Overview</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight flex items-center gap-2.5">
              <FiUser className="w-7 h-7 text-[#115B9D]" />
              Administrator Profile
            </h1>
            <p className="text-xs sm:text-sm text-slate-600 font-normal mt-0.5">
              View your account identity, security status, and platform credentials.
            </p>
          </div>

          {/* Header Action Buttons */}
          <div className="flex items-center gap-2.5 flex-wrap self-stretch sm:self-auto">
            <Link
              to="/change-password"
              className="px-4 py-2 rounded-xl text-xs font-bold text-slate-800 bg-slate-100 hover:bg-slate-200 transition-all border border-slate-200 shadow-xs flex items-center gap-1.5 cursor-pointer"
            >
              <FiLock className="w-3.5 h-3.5 text-[#115B9D]" />
              <span>Change Password</span>
            </Link>

            <Link
              to="/profile/edit"
              className="px-5 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-[#115B9D] via-[#0E528F] to-[#0A3E6E] hover:from-[#0E528F] hover:to-[#08335C] transition-all shadow-md shadow-[#115B9D]/20 flex items-center gap-1.5 cursor-pointer"
            >
              <FiEdit3 className="w-3.5 h-3.5" />
              <span>Edit Profile</span>
            </Link>
          </div>
        </div>

        {/* ======================================================== */}
        {/* 2. SUPER ADMIN HERO IDENTITY BANNER */}
        {/* ======================================================== */}
        <div className="bg-gradient-to-br from-[#071324] via-[#0E2C52] to-[#115B9D] rounded-2xl p-6 sm:p-8 text-white shadow-lg border border-slate-800 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-64 h-64 bg-sky-400/15 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-16 -left-16 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl pointer-events-none" />

          <div className="relative z-10 flex flex-col md:flex-row items-center md:items-center justify-between gap-6">

            {/* Left: Avatar + Identity Info */}
            <div className="flex flex-col sm:flex-row items-center sm:items-center gap-5 text-center sm:text-left">
              {/* Avatar circle */}
              <div className="relative group shrink-0">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt={fullName}
                    className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl object-cover border-3 border-white/30 shadow-xl bg-slate-800"
                  />
                ) : (
                  <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-2 border-white/25 flex items-center justify-center text-3xl font-black text-white shadow-xl">
                    {fullName.charAt(0).toUpperCase()}
                  </div>
                )}
                <Link
                  to="/profile/edit"
                  className="absolute -bottom-2 -right-2 w-8 h-8 rounded-xl bg-white text-[#115B9D] flex items-center justify-center shadow-lg hover:scale-105 transition-transform border border-slate-200 cursor-pointer"
                  title="Change Profile Picture"
                >
                  <FiEdit3 className="w-4 h-4" />
                </Link>
              </div>

              {/* Name, Email & Role Badges */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-2.5 flex-wrap justify-center sm:justify-start">
                  <h2 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
                    {fullName}
                  </h2>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-400/30 text-xs font-black uppercase tracking-wider text-emerald-300">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                    {roleName}
                  </span>
                </div>

                <div className="flex items-center gap-2 text-xs sm:text-sm text-sky-100/90 font-mono justify-center sm:justify-start">
                  <FiMail className="w-4 h-4 text-sky-300" />
                  <span>{email}</span>
                </div>

                <div className="flex items-center gap-3 text-xs text-sky-200/80 pt-1 flex-wrap justify-center sm:justify-start">
                  <span className="inline-flex items-center gap-1.5">
                    <FiShield className="w-3.5 h-3.5 text-sky-300" />
                    Super Admin Gateway
                  </span>
                  <span>&bull;</span>
                  <span className="inline-flex items-center gap-1.5">
                    <FiCheckCircle className="w-3.5 h-3.5 text-emerald-400" />
                    Identity Verified
                  </span>
                </div>
              </div>
            </div>

            {/* Right: Quick Action Pill */}
            <div className="flex flex-col sm:flex-row gap-2.5 w-full md:w-auto shrink-0 relative z-10">
              <Link
                to="/profile/edit"
                className="w-full sm:w-auto px-5 py-2.5 rounded-xl text-xs font-bold bg-white text-slate-900 hover:bg-sky-50 transition-all shadow-sm flex items-center justify-center gap-2 cursor-pointer"
              >
                <FiEdit3 className="w-4 h-4 text-[#115B9D]" />
                <span>Edit Profile Details</span>
              </Link>
            </div>

          </div>
        </div>

        {/* ======================================================== */}
        {/* 3. PROFILE DETAILS BENTO GRID */}
        {/* ======================================================== */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Card 1: Personal & Contact Details */}
          <div className="bg-white rounded-2xl p-6 border border-slate-200/90 shadow-[0_2px_10px_rgba(0,0,0,0.03)] space-y-5">
            <div className="flex items-center justify-between pb-3.5 border-b border-slate-100">
              <h3 className="text-base font-black text-slate-900 tracking-tight flex items-center gap-2">
                <FiUser className="w-4 h-4 text-[#115B9D]" />
                Personal Information
              </h3>
              <Link
                to="/profile/edit"
                className="text-xs font-bold text-[#115B9D] hover:underline flex items-center gap-1"
              >
                <span>Edit</span>
                <span>&rarr;</span>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80 space-y-1">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600 block">
                  Full Name
                </span>
                <span className="text-sm font-black text-slate-900 block truncate">
                  {fullName}
                </span>
              </div>

              <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80 space-y-1">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600 block">
                  Primary Email
                </span>
                <span className="text-sm font-black text-slate-900 block truncate font-mono">
                  {email}
                </span>
              </div>

              <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80 space-y-1">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600 block">
                  Mobile Number
                </span>
                <span className="text-sm font-black text-slate-900 block truncate">
                  {mobileNo || "Not Provided"}
                </span>
              </div>

              <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80 space-y-1">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600 block">
                  Office Address
                </span>
                <span className="text-sm font-black text-slate-900 block truncate">
                  {address || "Not Provided"}
                </span>
              </div>
            </div>
          </div>

          {/* Card 2: Security & Platform Credentials */}
          <div className="bg-white rounded-2xl p-6 border border-slate-200/90 shadow-[0_2px_10px_rgba(0,0,0,0.03)] space-y-5">
            <div className="flex items-center justify-between pb-3.5 border-b border-slate-100">
              <h3 className="text-base font-black text-slate-900 tracking-tight flex items-center gap-2">
                <FiShield className="w-4 h-4 text-[#115B9D]" />
                Security & Account Credentials
              </h3>
              <Link
                to="/change-password"
                className="text-xs font-bold text-[#115B9D] hover:underline flex items-center gap-1"
              >
                <span>Change Password</span>
                <span>&rarr;</span>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80 space-y-1">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600 block">
                  System Role
                </span>
                <span className="text-sm font-black text-slate-900 block">
                  {roleName} (Full Access)
                </span>
              </div>

              <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80 space-y-1">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600 block">
                  Account Status
                </span>
                <div className="pt-0.5">
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700 bg-emerald-100/70 border border-emerald-300/80 px-2.5 py-0.5 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    {status || "Active"}
                  </span>
                </div>
              </div>

              <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80 space-y-1">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600 block">
                  Account Created
                </span>
                <span className="text-xs font-bold text-slate-900 block">
                  {formatDate(profile?.createdAt)}
                </span>
              </div>

              <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80 space-y-1">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600 block">
                  Last Login Session
                </span>
                <span className="text-xs font-bold text-slate-900 block">
                  {formatDate(profile?.lastLoginDate)}
                </span>
              </div>
            </div>
          </div>

        </div>

        {/* ======================================================== */}
        {/* 4. SECURITY AUDIT BADGE */}
        {/* ======================================================== */}
        <div className="bg-sky-50/80 rounded-2xl p-5 border border-sky-200 text-xs text-sky-950 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#115B9D] text-white flex items-center justify-center shrink-0">
              <FiLock className="w-5 h-5" />
            </div>
            <div>
              <div className="font-bold text-slate-900 text-sm">Enterprise Account Protection</div>
              <p className="text-slate-600 text-xs mt-0.5">
                Your administrator account is shielded by 256-bit SSL encryption and strict session controls.
              </p>
            </div>
          </div>
          <Link
            to="/change-password"
            className="px-4 py-2 rounded-xl text-xs font-bold bg-[#115B9D] text-white hover:bg-[#0E528F] transition-all shadow-xs shrink-0 self-stretch sm:self-auto text-center"
          >
            Update Password
          </Link>
        </div>

      </div>
      <ToastContainer position="top-right" />
    </Layout>
  );
}
